import { clamp, forwardXZ, rightXZ } from '../sim/math.js';
import { centerOf } from './state.js';
import { defOf, emit, isLive, mech, predicting } from './tick.js';
import { otherSlot } from './types.js';
/** Damage factor by distance: flat to `falloffStart`, linear to `minDamageFactor` at `max`. */
export function falloffFactor(distance, range) {
    if (distance <= range.falloffStart)
        return 1;
    if (range.max <= range.falloffStart)
        return range.minDamageFactor;
    const t = clamp((distance - range.falloffStart) / (range.max - range.falloffStart), 0, 1);
    return 1 - (1 - range.minDamageFactor) * t;
}
/** Legs below the leg band; arms when a torso-band hit lies far enough off the axis sideways. */
export function hitLocationOf(hitPoint, target, def, legBand, armLateralFraction) {
    const height = def.capsule.height;
    const rel = (hitPoint.y - target.position.y) / height;
    if (rel < legBand)
        return 'legs';
    const right = rightXZ(target.yaw);
    const lateral = Math.abs((hitPoint.x - target.position.x) * right.x + (hitPoint.z - target.position.z) * right.z);
    return lateral > armLateralFraction * def.capsule.radius ? 'arms' : 'torso';
}
/** True when the damage source lies within the rear arc behind the target's facing. */
export function isRearHit(target, sourcePosition, rearArcDeg) {
    const back = forwardXZ(target.yaw);
    back.x = -back.x;
    back.z = -back.z;
    const dx = sourcePosition.x - target.position.x;
    const dz = sourcePosition.z - target.position.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-6)
        return false;
    const cos = (dx * back.x + dz * back.z) / len;
    return cos >= Math.cos(((rearArcDeg / 2) * Math.PI) / 180);
}
/**
 * True when a parry stance on `target` faces the source (blocks the attack). `pose` is the
 * target pose the attack was resolved against (rewound for instant attacks).
 */
export function parryFaces(t, target, sourcePosition, pose = mech(t, target).body) {
    const parry = t.mods[target].parry;
    if (!parry)
        return false;
    const f = forwardXZ(pose.aimYaw);
    const dx = sourcePosition.x - pose.position.x;
    const dz = sourcePosition.z - pose.position.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-6)
        return true;
    const cos = (dx * f.x + dz * f.z) / len;
    return cos >= Math.cos(((parry.arcDeg / 2) * Math.PI) / 180);
}
export function addUltCharge(t, slot, amount) {
    const c = mech(t, slot);
    if (!c.alive || t.mods[slot].noUltCharge || !isLive(t))
        return;
    const before = c.ultCharge;
    c.ultCharge = Math.min(100, c.ultCharge + amount);
    if (before < 100 && c.ultCharge >= 100)
        emit(t, { t: 'ultCharged', slot });
}
/** Removes cloak effects from a mech (fired a weapon, took damage, got revealed). */
export function breakCloak(t, slot) {
    const c = mech(t, slot);
    if (!c.effects.some((e) => e.params.cloak))
        return;
    for (const e of c.effects) {
        if (e.params.cloak)
            emit(t, { t: 'effectEnd', slot, kind: e.kind, id: e.id });
    }
    c.effects = c.effects.filter((e) => !e.params.cloak);
    t.mods[slot].cloaked = false;
}
export function kill(t, victim, killer) {
    const c = mech(t, victim);
    if (!c.alive)
        return;
    c.alive = false;
    c.hp = 0;
    c.effects = [];
    c.venting = false;
    t.s.stats[victim].deaths++;
    if (killer !== null && killer !== victim)
        t.s.stats[killer].kills++;
    if (t.s.firstBlood === null && killer !== null && killer !== victim) {
        t.s.firstBlood = killer;
        t.s.stats[killer].firstBlood = true;
    }
    emit(t, { t: 'kill', victim, killer });
}
/**
 * Applies one damage packet and returns the HP actually removed. Invulnerability and a facing
 * parry block it (except `direct` packets); shields absorb first; armor and the rear critical
 * apply to what reaches the hull. A client prediction applies nothing: damage is the server's.
 */
export function applyDamage(t, packet) {
    if (predicting(t))
        return 0;
    const target = mech(t, packet.target);
    if (!target.alive || packet.amount <= 0)
        return 0;
    const def = defOf(t, packet.target);
    const balance = t.ctx.balance;
    const tm = t.mods[packet.target];
    const self = packet.source === packet.target;
    if (!packet.direct) {
        if (tm.invulnerable) {
            emit(t, {
                t: 'blocked',
                target: packet.target,
                source: packet.source,
                position: packet.position,
            });
            return 0;
        }
        if (!self && parryFaces(t, packet.target, mech(t, packet.source).body.position, packet.pose)) {
            emit(t, { t: 'parry', slot: packet.target, position: packet.position });
            if (packet.kind === 'melee') {
                const stunTicks = tm.parry?.stunTicks ?? 0;
                if (stunTicks > 0 && !t.mods[packet.source].ccImmune) {
                    mech(t, packet.source).effects.push({
                        id: t.s.nextId++,
                        kind: 'stun',
                        source: packet.target,
                        ticksLeft: stunTicks,
                        params: { flags: ['stunned'] },
                    });
                    emit(t, {
                        t: 'effectStart',
                        slot: packet.source,
                        kind: 'stun',
                        id: t.s.nextId - 1,
                        ticks: stunTicks,
                    });
                }
            }
            return 0;
        }
    }
    let amount = packet.amount * tm.damageTakenScale;
    if (!self)
        amount *= t.mods[packet.source].damageScale;
    let shieldDamage = 0;
    let hpDamage;
    if (packet.direct) {
        hpDamage = amount;
    }
    else {
        if (target.shield > 0) {
            shieldDamage = Math.min(target.shield, amount);
            target.shield -= shieldDamage;
            if (target.shield <= 1e-9) {
                target.shield = 0;
                emit(t, { t: 'shieldBroken', slot: packet.target });
            }
        }
        target.shieldDelayTicks = Math.round(balance.shieldRegenDelaySeconds * 30);
        const remaining = amount - shieldDamage;
        const armor = clamp(def.armor[packet.location] + tm.armorBonus, 0, balance.armor.max);
        const crit = packet.rear && packet.location === 'torso';
        hpDamage = remaining * (1 - armor) * (crit ? balance.hitLocation.rearCritMultiplier : 1);
        if (packet.location === 'legs')
            target.legDamage += hpDamage;
        else if (packet.location === 'arms')
            target.armDamage += hpDamage;
    }
    hpDamage = Math.min(target.hp, hpDamage);
    target.hp -= hpDamage;
    if (packet.kind === 'suddenDeath' || packet.kind === 'hazard') {
        // The arena's doing: no statistics, no ultimate charge, no killer. A hazard still reports
        // its hit so the HUD and the match log see the damage; the drain stays silent.
        if (packet.kind === 'hazard') {
            emit(t, {
                t: 'hit',
                target: packet.target,
                source: packet.source,
                kind: packet.kind,
                hp: hpDamage,
                shield: shieldDamage,
                location: packet.location,
                rear: false,
                crit: false,
                position: packet.position,
            });
        }
        if (target.hp <= 1e-9)
            kill(t, packet.target, null);
        return hpDamage;
    }
    const dealt = hpDamage + shieldDamage;
    if (self) {
        t.s.stats[packet.target].selfDamage += dealt;
    }
    else {
        t.s.stats[packet.source].damageDealt += dealt;
        addUltCharge(t, packet.source, dealt * balance.ultimate.perDamageDealt);
    }
    t.s.stats[packet.target].damageTaken += dealt;
    addUltCharge(t, packet.target, dealt * balance.ultimate.perDamageTaken);
    if (!self && dealt > 0)
        breakCloak(t, packet.target);
    emit(t, {
        t: 'hit',
        target: packet.target,
        source: packet.source,
        kind: packet.kind,
        hp: hpDamage,
        shield: shieldDamage,
        location: packet.location,
        rear: packet.rear,
        crit: packet.rear && packet.location === 'torso' && !packet.direct,
        position: packet.position,
    });
    if (target.hp <= 1e-9)
        kill(t, packet.target, self ? null : packet.source);
    return hpDamage;
}
/** Torso hit with rear judged from the source position: splash, melee, abilities. */
export function areaPacket(t, target, source, amount, kind, sourcePosition) {
    const c = mech(t, target);
    return {
        target,
        source,
        amount,
        kind,
        location: 'torso',
        rear: isRearHit(c.body, sourcePosition, t.ctx.balance.hitLocation.rearArcDeg),
        position: centerOf(c.body, defOf(t, target)),
    };
}
/**
 * A located hit at a world point from a shooter. `pose` is the target pose the hit was found
 * against (the rewound one under lag compensation) so the location and rear arc agree with it.
 */
export function pointPacket(t, target, source, amount, kind, point, sourcePosition, pose = mech(t, target).body) {
    const balance = t.ctx.balance;
    return {
        target,
        source,
        amount,
        kind,
        location: hitLocationOf(point, pose, defOf(t, target), balance.hitLocation.legBand, balance.hitLocation.armLateralFraction),
        rear: isRearHit(pose, sourcePosition, balance.hitLocation.rearArcDeg),
        position: point,
        pose,
    };
}
export function enemyOf(slot) {
    return otherSlot(slot);
}
//# sourceMappingURL=damage.js.map